import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { SendOtpWithEmailRedirect } from '@/modules/auth/use-cases'
import {
  formatResendLabel,
  readDeadlineOfKind,
  useOtpSendCooldown,
  useSubmitGuard,
} from '@/shared/ui/hooks'
import { OtpSendThrottledError, formatCountdown } from '@/shared/domain'
import { reportError } from '@/shared/infra/observability'

const SEND_VERIFICATION_EMAIL_FAILED_MESSAGE =
  'We could not send the verification email. Please try again.'

export type UseVerifyEmailPageProps = {
  onSendOtpWithEmailRedirect: SendOtpWithEmailRedirect
}

export type VerifyEmailStatus = 'loading' | 'sent' | 'error'

export type UseVerifyEmailPageResult = {
  status: VerifyEmailStatus
  email: string
  resendSeconds: number
  resendLabel: string
  resendDisabled: boolean
  errorMessage: string
  handleResend: () => Promise<void>
}

type PendingClub = {
  orgId: string
  email: string
}

function readPendingClub(): { value: PendingClub | null; invalid: boolean } {
  const raw = window.localStorage.getItem('pendingClub')
  if (!raw) return { value: null, invalid: false }
  try {
    const parsed = JSON.parse(raw) as PendingClub
    if (typeof parsed?.orgId !== 'string' || typeof parsed?.email !== 'string') {
      return { value: null, invalid: true }
    }
    return { value: parsed, invalid: false }
  } catch {
    return { value: null, invalid: true }
  }
}

function buildRedirectTo(orgId: string): string {
  return `${window.location.origin}/auth/callback?redirect_to=/auth/link-club&org_id=${orgId}`
}

const MS_PER_SECOND = 1000

function describeLockedSend(deadline: number): string {
  const secondsLeft = Math.max(1, Math.ceil((deadline - Date.now()) / MS_PER_SECOND))
  return `Too many emails have been sent. Try again in ${formatCountdown(secondsLeft)}.`
}

function describeSendFailure(err: unknown): string {
  if (err instanceof OtpSendThrottledError) return err.linkMessage
  return reportError(err, { fallbackMessage: SEND_VERIFICATION_EMAIL_FAILED_MESSAGE })
}

export function useVerifyEmailPage({
  onSendOtpWithEmailRedirect,
}: UseVerifyEmailPageProps): UseVerifyEmailPageResult {
  const navigate = useNavigate()
  const startedRef = useRef(false)
  const pendingRef = useRef<PendingClub | null>(null)
  const [status, setStatus] = useState<VerifyEmailStatus>('loading')
  const [email, setEmail] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const { runGuarded } = useSubmitGuard()
  const {
    secondsLeft: resendSeconds,
    applyRefusal,
    recordSend,
  } = useOtpSendCooldown(email !== '' ? { channel: 'email', identifier: email } : null)

  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true

    const pending = readPendingClub()
    if (pending.invalid || !pending.value) {
      navigate('/register', { replace: true })
      return
    }

    pendingRef.current = pending.value
    setEmail(pending.value.email)

    const target = { channel: 'email' as const, identifier: pending.value.email }
    if (readDeadlineOfKind('send', target.channel, target.identifier) !== null) {
      setStatus('sent')
      return
    }
    const refusalDeadline = readDeadlineOfKind('refusal', target.channel, target.identifier)
    if (refusalDeadline !== null) {
      setErrorMessage(describeLockedSend(refusalDeadline))
      setStatus('error')
      return
    }

    void runGuarded(async () => {
      try {
        await onSendOtpWithEmailRedirect({
          email: target.identifier,
          emailRedirectTo: buildRedirectTo(pending.value!.orgId),
        })
        recordSend(target)
        setStatus('sent')
      } catch (err) {
        const message = describeSendFailure(err)
        setStatus('error')
        setErrorMessage(message)
        applyRefusal(err, target)
      }
    })
  }, [applyRefusal, navigate, onSendOtpWithEmailRedirect, recordSend, runGuarded])

  const handleResend = async () => {
    const pending = pendingRef.current
    if (!pending) return
    if (status === 'loading' || resendSeconds > 0) return
    await runGuarded(async () => {
      try {
        setErrorMessage('')
        await onSendOtpWithEmailRedirect({
          email: pending.email,
          emailRedirectTo: buildRedirectTo(pending.orgId),
        })
        recordSend()
        setStatus('sent')
      } catch (err) {
        const message = describeSendFailure(err)
        setStatus('error')
        setErrorMessage(message)
        applyRefusal(err)
      }
    })
  }

  return {
    status,
    email,
    resendSeconds,
    resendLabel: formatResendLabel(resendSeconds),
    resendDisabled: status === 'loading' || resendSeconds > 0,
    errorMessage,
    handleResend,
  }
}
